// ===============================================================
// Forms — Responses Viewer
// Browse, filter, search, and inspect form responses
// ===============================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Table, Modal, Stack, Loader, Center, ActionIcon, Image, Tooltip,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconDownload, IconEye, IconFileTypePdf,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useFormsStore } from '../../store/formsStore';
import { FIELDS } from '../../lib/api-switch';
import type { FormResponse, FormSchema, FormField } from '../../types/forms';
import { fmtDateTime as fmtDate } from '../../lib/dateFormat';

// ── Helpers ─────────────────────────────────────────────────────

function statusColor(s: string): string {
  switch (s) {
    case 'complete': return 'green';
    case 'partial': return 'yellow';
    case 'reviewed': return 'blue';
    default: return 'gray';
  }
}

function exportCSV(responses: FormResponse[]) {
  if (responses.length === 0) return;
  // Collect all unique field keys from responseData across responses
  const allKeys = new Set<string>();
  const parsed = responses.map(r => {
    try { const d = JSON.parse(r.responseData); Object.keys(d).forEach(k => allKeys.add(k)); return d; }
    catch { return {}; }
  });
  const fieldKeys = Array.from(allKeys);
  const headers = ['Form Name', 'Respondent', 'Email', 'Incident', 'Submitted', 'Status', ...fieldKeys];

  const rows = responses.map((r, i) => {
    const d = parsed[i];
    return [
      r.templateName, r.respondentName, r.respondentEmail, r.incidentId,
      r.submittedAt, r.status,
      ...fieldKeys.map(k => {
        const v = d[k];
        if (v == null) return '';
        if (typeof v === 'string' && v.startsWith('data:image')) return '[photo]';
        return String(v);
      }),
    ];
  });

  const csv = [headers, ...rows]
    .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `form-responses-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Detail Modal ────────────────────────────────────────────────

function ResponseDetail({ response, onClose, onStatusChange }: {
  response: FormResponse; onClose: () => void;
  onStatusChange?: (rid: number, status: string) => void;
}) {
  let data: Record<string, unknown> = {};
  try { data = JSON.parse(response.responseData); } catch { /* empty */ }

  // Try to get field labels AND option labels from the matching template schema
  const { templates } = useFormsStore.getState();
  const tpl = templates.find(t => String(t.Rid) === String(response.templateId) || String(t.id) === String(response.templateId));
  const fieldLabels: Record<string, string> = {};
  const optionLabels: Record<string, Record<string, string>> = {}; // fieldKey → { optValue → optLabel }
  const fieldTypes: Record<string, string> = {};
  if (tpl) {
    try {
      const schema: FormSchema = JSON.parse((tpl as any).schemaJson || (tpl as any).schema || '{"pages":[]}');
      schema.pages.forEach(p => p.fields.forEach((f: FormField) => {
        fieldLabels[f.key] = f.label || f.key;
        fieldTypes[f.key] = f.type;
        if (f.options?.length) {
          optionLabels[f.key] = {};
          f.options.forEach(o => { optionLabels[f.key][o.value] = o.label; });
        }
      }));
    } catch { /* ignore */ }
  }

  // Resolve an option value to its human-readable label
  function resolveOption(fieldKey: string, val: string): string {
    return optionLabels[fieldKey]?.[val] || val;
  }

  return (
    <Modal opened onClose={onClose} title={response.templateName || 'Response Detail'} size="lg" centered>
      <Stack gap="sm">
        <Group gap="lg">
          <div>
            <Text size="xs" c="dimmed">Respondent</Text>
            <Text size="sm" fw={500}>{response.respondentName || 'Anonymous'}</Text>
          </div>
          {response.respondentEmail && (
            <div>
              <Text size="xs" c="dimmed">Email</Text>
              <Text size="sm">{response.respondentEmail}</Text>
            </div>
          )}
          <div>
            <Text size="xs" c="dimmed">Submitted</Text>
            <Text size="sm">{fmtDate(response.submittedAt)}</Text>
          </div>
          <Badge color={statusColor(response.status)}>{response.status}</Badge>
        </Group>

        <Paper p="md" bg="gray.0" radius="sm" withBorder>
          <Stack gap="xs">
            {Object.entries(data).map(([key, value]) => {
              const label = fieldLabels[key] || key;
              const fType = fieldTypes[key];

              // Normalise: JSON-encoded arrays stored as strings
              let resolved = value;
              if (typeof value === 'string' && value.startsWith('[')) {
                try { resolved = JSON.parse(value); } catch { /* keep as string */ }
              }

              const strVal = String(resolved ?? '');

              // Signature (base64 image)
              if (fType === 'signature' && typeof resolved === 'string' && resolved.startsWith('data:image')) {
                return (
                  <div key={key}>
                    <Text size="xs" fw={600} c="dimmed">{label}</Text>
                    <Image src={resolved} maw={240} radius="sm" mt={4} />
                  </div>
                );
              }

              // Photo (base64 image)
              if (typeof resolved === 'string' && resolved.startsWith('data:image')) {
                return (
                  <div key={key}>
                    <Text size="xs" fw={600} c="dimmed">{label}</Text>
                    <Image src={resolved} maw={320} radius="sm" mt={4} />
                  </div>
                );
              }

              // GPS coordinates
              if (typeof resolved === 'object' && resolved !== null && 'lat' in (resolved as Record<string, unknown>)) {
                const gps = resolved as { lat: number; lng: number; accuracy?: number };
                return (
                  <div key={key}>
                    <Text size="xs" fw={600} c="dimmed">{label}</Text>
                    <Text size="sm">
                      {gps.lat.toFixed(6)}, {gps.lng.toFixed(6)}
                      {gps.accuracy ? ` (${Math.round(gps.accuracy)}m accuracy)` : ''}
                    </Text>
                  </div>
                );
              }

              // Likert (object with row keys)
              if (fType === 'likert' && typeof resolved === 'object' && resolved !== null && !Array.isArray(resolved)) {
                const field = tpl ? (() => {
                  try {
                    const s: FormSchema = JSON.parse((tpl as any).schemaJson || (tpl as any).schema || '{}');
                    return s.pages.flatMap(p => p.fields).find((f: FormField) => f.key === key);
                  } catch { return undefined; }
                })() : undefined;
                const rows = (field as any)?.likertRows ?? [];
                const entries = Object.entries(resolved as Record<string, unknown>);
                return (
                  <div key={key}>
                    <Text size="xs" fw={600} c="dimmed" mb={4}>{label}</Text>
                    <Stack gap={2}>
                      {entries.map(([rk, rv], i) => (
                        <Group key={rk} justify="space-between" gap="xs">
                          <Text size="xs" c="dimmed">{rows[i] ?? rk}</Text>
                          <Badge size="xs" variant="light">{String(rv)}/5</Badge>
                        </Group>
                      ))}
                    </Stack>
                  </div>
                );
              }

              // Arrays (checkboxes, multi-select) — resolve option labels
              if (Array.isArray(resolved)) {
                if (resolved.length === 0) return (
                  <div key={key}>
                    <Text size="xs" fw={600} c="dimmed">{label}</Text>
                    <Text size="sm" c="dimmed">—</Text>
                  </div>
                );
                return (
                  <div key={key}>
                    <Text size="xs" fw={600} c="dimmed">{label}</Text>
                    <Group gap={4} mt={2}>
                      {resolved.map((v, i) => (
                        <Badge key={i} size="sm" variant="light">{resolveOption(key, String(v))}</Badge>
                      ))}
                    </Group>
                  </div>
                );
              }

              // Radio/select — resolve option label
              if ((fType === 'radio' || fType === 'select') && optionLabels[key]) {
                return (
                  <div key={key}>
                    <Text size="xs" fw={600} c="dimmed">{label}</Text>
                    <Text size="sm">{resolveOption(key, strVal)}</Text>
                  </div>
                );
              }

              // Default
              return (
                <div key={key}>
                  <Text size="xs" fw={600} c="dimmed">{label}</Text>
                  <Text size="sm">{strVal || '—'}</Text>
                </div>
              );
            })}
            {Object.keys(data).length === 0 && (
              <Text size="sm" c="dimmed" ta="center">No response data</Text>
            )}
          </Stack>
        </Paper>

        {response.notes && (
          <div>
            <Text size="xs" fw={600} c="dimmed">Notes</Text>
            <Text size="sm">{response.notes}</Text>
          </div>
        )}

        <Group justify="space-between" mt="sm">
          {onStatusChange ? (
            <Group gap="xs">
              <Text size="xs" c="dimmed">Mark as:</Text>
              {response.status !== 'reviewed' && (
                <Button size="xs" variant="light" color="blue"
                  onClick={() => { onStatusChange(response.Rid || response.id, 'reviewed'); onClose(); }}>
                  Reviewed
                </Button>
              )}
              {response.status !== 'complete' && (
                <Button size="xs" variant="light" color="green"
                  onClick={() => { onStatusChange(response.Rid || response.id, 'complete'); onClose(); }}>
                  Complete
                </Button>
              )}
            </Group>
          ) : <div />}
          <Button variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Main Component ──────────────────────────────────────────────

export default function FormResponses() {
  const { templates, responses, loading, loadAll, updateResponse } = useFormsStore();

  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState<string | null>('all');
  const [selected, setSelected] = useState<FormResponse | null>(null);

  useEffect(() => { loadAll(); }, []);

  // Template filter dropdown data
  const templateOptions = useMemo(() => [
    { value: 'all', label: 'All Templates' },
    ...templates.map(t => ({ value: String(t.Rid), label: String(t.templateName || 'Untitled') })),
  ], [templates]);

  // Sort state
  const [sortField, setSortField] = useState<string>('submittedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState('all');

  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (field: string) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Filtered + searched + sorted responses (enriched with template name)
  const filtered = useMemo(() => {
    return [...responses]
      .map(r => {
        const tpl = templates.find(t => String(t.id) === String(r.templateId) || String(t.Rid) === String(r.templateId));
        return { ...r, templateName: r.templateName || (tpl as any)?.templateName || (tpl as any)?.title || '' };
      })
      .filter(r => {
        if (templateFilter && templateFilter !== 'all' && String(r.templateId) !== templateFilter) return false;
        if (statusFilter !== 'all' && r.status !== statusFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          if (
            !(r.respondentName || '').toLowerCase().includes(q) &&
            !(r.templateName || '').toLowerCase().includes(q)
          ) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const dir = sortDir === 'asc' ? 1 : -1;
        if (sortField === 'submittedAt') {
          const da = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
          const db = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
          return (da - db) * dir;
        }
        const va = (a as any)[sortField] || '';
        const vb = (b as any)[sortField] || '';
        return String(va).localeCompare(String(vb)) * dir;
      });
  }, [responses, templateFilter, statusFilter, search, sortField, sortDir]);

  // Status breakdown
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { complete: 0, partial: 0, reviewed: 0 };
    filtered.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [filtered]);

  if (loading) {
    return <Center p="xl"><Loader /></Center>;
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Title order={3} c="black">Responses</Title>
        </Group>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconDownload size={14} />}
          onClick={() => exportCSV(filtered as any)}
          disabled={filtered.length === 0}
        >
          Export CSV
        </Button>
      </Group>

      {/* Filters */}
      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Search by respondent..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 320 }}
          size="sm"
        />
        <Select
          data={templateOptions}
          value={templateFilter}
          onChange={setTemplateFilter}
          placeholder="Filter by template"
          size="sm"
          w={240}
          clearable={false}
        />
        <Select
          data={[
            { value: 'all', label: 'All Statuses' },
            { value: 'complete', label: 'Complete' },
            { value: 'partial', label: 'Partial' },
            { value: 'reviewed', label: 'Reviewed' },
          ]}
          value={statusFilter}
          onChange={v => setStatusFilter(v ?? 'all')}
          size="sm"
          w={160}
        />
      </Group>

      {/* Stats */}
      <Group mb="md" gap="sm">
        <Badge variant="light" color="gray" size="lg">{filtered.length} total</Badge>
        {statusCounts.complete > 0 && <Badge variant="light" color="green" size="sm">{statusCounts.complete} complete</Badge>}
        {statusCounts.partial > 0 && <Badge variant="light" color="yellow" size="sm">{statusCounts.partial} partial</Badge>}
        {statusCounts.reviewed > 0 && <Badge variant="light" color="blue" size="sm">{statusCounts.reviewed} reviewed</Badge>}
      </Group>

      {/* Table */}
      <Paper radius="sm" withBorder style={{ overflow: 'auto', maxHeight: 600 }}>
        <Table striped highlightOnHover stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('templateName')}>Form Name{sortIcon('templateName')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('respondentName')}>Respondent{sortIcon('respondentName')}</Table.Th>
              <Table.Th>Incident</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('submittedAt')}>Submitted{sortIcon('submittedAt')}</Table.Th>
              <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
              <Table.Th w={100}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text size="sm" c="dimmed" ta="center" py="lg">No responses found</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filtered.map(r => (
                <Table.Tr
                  key={r.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelected(r as any)}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>{r.templateName || 'Untitled'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.respondentName || 'Anonymous'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{String(r.incidentId || '—')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{fmtDate(r.submittedAt)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={statusColor(r.status)}>{r.status}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Tooltip label="View details">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setSelected(r); }}
                        >
                          <IconEye size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Export PDF">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            // PDF export placeholder — could use browser print
                            window.print();
                          }}
                        >
                          <IconFileTypePdf size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Detail Modal */}
      {selected && <ResponseDetail response={selected} onClose={() => setSelected(null)}
        onStatusChange={async (rid, status) => {
          try {
            await updateResponse(rid, [[FIELDS.formResponses.status, status]]);
            notifications.show({ title: 'Updated', message: `Marked as ${status}`, color: 'green' });
            loadAll();
          } catch {
            notifications.show({ title: 'Error', message: 'Failed to update status', color: 'red' });
          }
        }}
      />}
    </div>
  );
}
